import { useCallback, useEffect } from 'react';

import { BindingCreator, FormContextValues } from '@dreryk/react-common';
import { Button, ButtonVariant, Checkbox, StyledFlex, TestId } from '@dreryk/react-components';

import { referralToPsychiatricHospitalTranslationKeys } from '../../../../translations';

import { GuardianAddressSection } from './GuardianAddressSection';
import { GuardianNameSection } from './GuardianNameSection';
import { StyledEnterDataOnceButton, StyledGuardianSection } from './GuardianSection.styled';
import {
   GuardianFieldName,
   GuardianFormBeanPart,
   GuardianNotApplicableFieldName,
   GuardianWithAddressFormBeanPart,
} from './guardianSection.types';
import { isGuardianAddressSectionVisible, isGuardianNameSectionVisible } from './guardianSection.utils';
import { GuardianSelect } from './GuardianSelect/GuardianSelect';

type GuardianSectionProps<FIELD_NAME extends GuardianFieldName> = Pick<
   FormContextValues<GuardianFormBeanPart>,
   'watch' | 'setValue' | 'trigger'
> & {
   readonly name: FIELD_NAME;
   readonly patientId: number;
   readonly register: BindingCreator<GuardianFormBeanPart, GuardianFieldName | GuardianNotApplicableFieldName>;
   readonly testId: TestId;
};

export const GuardianSection = <FIELD_NAME extends GuardianFieldName>(
   props: GuardianSectionProps<FIELD_NAME>
): JSX.Element => {
   const currentGuardian = props.watch(props.name);
   const notApplicableFieldName = `${props.name}NotApplicable` as const;
   const currentGuardianNotApplicable = props.watch(notApplicableFieldName);
   const { register } = props;

   useEffect(() => {
      if (currentGuardianNotApplicable) {
         props.trigger(props.name);
         if (currentGuardian !== null) {
            props.setValue(props.name, null);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentGuardianNotApplicable]);

   const handleEnterDataOnce = useCallback(() => {
      props.setValue(props.name, 'ENTERED_BY_USER', { shouldDirty: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.setValue]);

   return (
      <StyledGuardianSection>
         <StyledFlex direction={'column'} gap={'8px'}>
            <GuardianSelect
               testId={props.testId}
               name={props.name}
               patientId={props.patientId}
               register={props.register}
               disabled={currentGuardianNotApplicable}
            />
            <Checkbox
               testId={`${props.testId}NotApplicable`}
               controller={props.register(notApplicableFieldName)}
               label={referralToPsychiatricHospitalTranslationKeys.notApplicable}
            />
         </StyledFlex>
         <StyledEnterDataOnceButton>
            {currentGuardianNotApplicable || currentGuardian === 'ENTERED_BY_USER' ? null : (
               <Button
                  testId={`${props.testId}EnterDataOnce`}
                  label={referralToPsychiatricHospitalTranslationKeys.enterDataOnce}
                  variant={ButtonVariant.SECONDARY}
                  onClick={handleEnterDataOnce}
               />
            )}
         </StyledEnterDataOnceButton>
         {isGuardianNameSectionVisible(currentGuardian, currentGuardianNotApplicable, register) ? (
            <GuardianNameSection testId={props.testId} name={props.name} register={register} />
         ) : null}
         {isGuardianAddressSectionVisible(currentGuardian, currentGuardianNotApplicable, register) ? (
            <GuardianAddressSection<FIELD_NAME>
               name={props.name}
               testId={props.testId}
               register={register}
               watch={props.watch as FormContextValues<GuardianWithAddressFormBeanPart>['watch']}
               setValue={props.setValue}
               trigger={props.trigger}
            />
         ) : null}
      </StyledGuardianSection>
   );
};
